/**
 * Kirby Resources Page
 *
 * Single-day view showing games scheduled for the selected date.
 * Displays resource states (gateway, TM, SES) and manual cleanup controls.
 */

import { useMemo, useState } from 'react';
import { addDays, format, parseISO, startOfDay, isSameDay } from 'date-fns';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DatePicker } from '@mui/x-date-pickers';

import AppPage from '@/shared/components/PageComponents/AppPage';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { useGames } from '@/features/games/hooks/gameQueries';
import useSeasonIdForDate from '@/features/games/hooks/useSeasonIdForDate';

import { ResourceCard } from './ResourceCard';
import { useCanEditResources } from '../hooks/useCanEditResources';
import { useKirbyMode } from '../hooks/useKirbyMode';
import PageHeader from '@/shared/components/PageHeader';

export default function ResourcesPage() {
  const title = 'Resources';
  useDocumentTitle(title);

  const canEdit = useCanEditResources();
  const { mode: kirbyMode } = useKirbyMode();

  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));

  // The games API requires a season, and this view only knows a day — so the
  // season is resolved from the selected date rather than pinned, keeping the
  // date picker free to jump into earlier seasons.
  const { seasonId, isLoading: isSeasonLoading } = useSeasonIdForDate(selectedDate);

  // Fetch games for the selected day
  const { data, isLoading: isGamesLoading, error } = useGames({
    seasonId,
    date: selectedDate,
    streamTypes: ['live', 'recorded', 'scheduled', 'delayed'],
    limit: 50,
    upcomingLimit: 50,
  });

  // The season lookup gates the games query, so both have to settle before the
  // page is entitled to say there are no games that day.
  const isLoading = isGamesLoading || isSeasonLoading;

  const todayGames = useMemo(() => {
    const games = data?.games ?? [];
    return games
      .filter((g: any) => {
        if (!g.startTimestamp) return false;
        const ts = typeof g.startTimestamp === 'string' ? parseISO(g.startTimestamp) : new Date(g.startTimestamp);
        return isSameDay(ts, selectedDate);
      })
      .sort((a: any, b: any) => {
        const ta = typeof a.startTimestamp === 'string' ? parseISO(a.startTimestamp).getTime() : new Date(a.startTimestamp).getTime();
        const tb = typeof b.startTimestamp === 'string' ? parseISO(b.startTimestamp).getTime() : new Date(b.startTimestamp).getTime();
        return ta - tb;
      });
  }, [data?.games, selectedDate]);

  const isToday = isSameDay(selectedDate, new Date());
  const handlePrevDay = () => setSelectedDate((d) => addDays(d, -1));
  const handleNextDay = () => setSelectedDate((d) => addDays(d, 1));
  const handleJumpToToday = () => setSelectedDate(startOfDay(new Date()));

  return (
    <AppPage title={title} requiredPermissions={['menu:games:resources']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader
          title="Monitor Kirby Resources"
          subtitle="Track SIP radius gateway, TM stacks, and SES ingester status for live games"
          action={
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
              KIRBY MODE:{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {kirbyMode}
              </Box>
            </Typography>
          }
        />

        {kirbyMode === 'off' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Kirby is disabled in this environment. Recorded resource status is
            still shown, but launching, cleanup and reset are unavailable.
          </Alert>
        )}

        {!canEdit && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Read-only access. Resource status is shown for every game, but
            launching and cleanup are not available to your account.
          </Alert>
        )}

        {/* Day navigator */}
        <Stack direction="row" sx={{ gap: 2, mb: 3, alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={handlePrevDay} aria-label="Previous day" size="large">
            <ChevronLeftIcon />
          </IconButton>

          <Box sx={{ minWidth: 200, textAlign: 'center' }}>
            <DatePicker
              value={dayjs(selectedDate)}
              onChange={(date) => date && setSelectedDate(startOfDay(date.toDate()))}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  onClick: (e) => e.preventDefault(),
                  sx: { cursor: 'pointer' },
                },
              }}
            />
          </Box>

          <IconButton onClick={handleNextDay} aria-label="Next day" size="large">
            <ChevronRightIcon />
          </IconButton>

          {!isToday && (
            <Button variant="outlined" size="small" onClick={handleJumpToToday}>
              Today
            </Button>
          )}
        </Stack>

        {/* Content */}
        {isLoading && (
          <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load games: {(error as any)?.message}
          </Alert>
        )}

        {!isLoading && todayGames.length === 0 && (
          <Alert severity="info">No games scheduled for {format(selectedDate, 'MMM dd, yyyy')}</Alert>
        )}

        {!isLoading && todayGames.length > 0 && (
          <Stack sx={{ gap: 2 }}>
            {todayGames.map((game: any) => (
              <ResourceCard key={game.id} game={game} />
            ))}
          </Stack>
        )}
      </Container>
    </AppPage>
  );
}
